import {
  SearchableSelect,
  SearchableSelectProps,
} from '@app/components/Fields/SearchableSelect';
import { getBusinessesAdmin } from '@app/services/admin-business';
import { useDebounce } from '@app/utils/use-debounce';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';

type Props = Pick<
  SearchableSelectProps,
  'onChange' | 'value' | 'sizeStyle' | 'className' | 'placeholder' | 'name'
>;

export const BusinessesSelect = (props: Props) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const {
    data: businessesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isBusinessesLoading,
  } = useInfiniteQuery({
    queryKey: ['businesses', 'admin', 'infinite', { search: debouncedSearch }],
    queryFn: ({ pageParam = 1 }) =>
      getBusinessesAdmin(pageParam, 20, debouncedSearch || undefined),
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil((lastPage?.total || 0) / lastPage.perPage);
      const nextPage =
        lastPage.page < totalPages ? lastPage.page + 1 : undefined;

      return nextPage;
    },
    initialPageParam: 1,
    placeholderData: keepPreviousData,
  });

  const businessOptions = (businessesData?.pages || []).flatMap((page) =>
    (page?.data || []).map((business) => ({
      value: business.id,
      label: business.name,
    }))
  );

  return (
    <SearchableSelect
      {...props}
      className={props.className || 'w-[300px]'}
      fetchNextPage={fetchNextPage}
      hasMore={!!hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isLoading={isBusinessesLoading}
      onSearch={(value) => setSearch(value)}
      options={businessOptions}
      placeholder={props.placeholder || 'All Businesses'}
      sizeStyle={props.sizeStyle || 'sm'}
    />
  );
};
